/**
 * Silent audio keep-alive for background/lock-screen resilience.
 *
 * Iteration 69 — Locking the phone silenced the ambient mix because iOS
 * Safari (and, on some builds, Android Chrome) suspend the AudioContext
 * when the tab has no ACTIVE `<audio src="...">` element with real media
 * content. The dialogue `<audio>` keeps a session alive while it plays
 * chunks, but between segments (or before the first chunk lands) the
 * session goes idle and the OS starts suspending WebAudio.
 *
 * Fix: a persistent HTMLAudio element playing a looped SILENT MP3 with a
 * VERY LOW (but non-zero) volume. Non-muted + real media source is the
 * signal the browser uses to keep the audio session alive. Users cannot
 * hear it (volume 0.001) but the browser considers audio "playing" the
 * entire time.
 *
 * The MP3 payload is a 0.5-second true-silence AAC-in-MP4 clip encoded
 * inline as a data URL — no network fetch, no extra asset to ship. The
 * element is created ONCE per page (idempotent) and is released via
 * `stopKeepAlive()` on session end.
 */

// 0.5s silent MP3 (48 kbps mono, ISO-BMFF header + 24 silence frames).
// Base64 payload is ~2.3 KB — small enough to inline. Sourced from the
// well-known silent-mp3 utility (public domain).
const SILENT_MP3 =
  "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

let _keepAliveEl = null;

/**
 * Start (or return the existing) silent keep-alive element.
 * Safe to call multiple times — subsequent calls are a no-op.
 * Must be called from a user gesture callback the FIRST time or the
 * browser may reject the autoplay; subsequent visibility changes are fine.
 */
export function startKeepAlive() {
  if (_keepAliveEl) {
    // Element already exists — ensure it's actually playing.
    try {
      const p = _keepAliveEl.play();
      if (p && p.catch) p.catch(() => { /* will retry on next visibility change */ });
    } catch (e) { /* ignore */ }
    return _keepAliveEl;
  }
  const el = document.createElement("audio");
  el.src = SILENT_MP3;
  el.loop = true;
  el.preload = "auto";
  el.autoplay = true;
  el.setAttribute("playsinline", "");
  // Important: NOT muted. `muted=true` disables the media-session heuristic
  // some browsers use to keep the tab's audio pipeline alive.
  el.muted = false;
  el.volume = 0.001;
  // Off-screen but IN the DOM & IN the flow — `display: none` can trigger
  // the browser to treat the element as inactive on some platforms, and
  // `<audio>` without `controls` defaults to `display: none` via the UA
  // stylesheet, so an explicit `display: block` override is required for
  // iOS Safari to keep the element active.
  el.style.display = "block";
  el.style.position = "absolute";
  el.style.width = "1px";
  el.style.height = "1px";
  el.style.opacity = "0";
  el.style.pointerEvents = "none";
  el.style.left = "-9999px";
  el.setAttribute("data-testid", "audio-session-keepalive");
  document.body.appendChild(el);
  const p = el.play();
  if (p && p.catch) p.catch(() => { /* will retry on next visibility change */ });

  // Re-nudge on every visibility change — the OS may deprioritize the
  // element the moment the tab hides; a manual play() ensures the media
  // session stays awake through the transition.
  const onVis = () => {
    try {
      const pp = el.play();
      if (pp && pp.catch) pp.catch(() => { /* ignore */ });
    } catch (e) { /* ignore */ }
  };
  document.addEventListener("visibilitychange", onVis);
  el._visHandler = onVis;
  _keepAliveEl = el;
  return el;
}

/** Stop and remove the keep-alive element (session end / unmount). */
export function stopKeepAlive() {
  if (!_keepAliveEl) return;
  try {
    if (_keepAliveEl._visHandler) {
      document.removeEventListener("visibilitychange", _keepAliveEl._visHandler);
    }
    _keepAliveEl.pause();
    _keepAliveEl.src = "";
    _keepAliveEl.remove();
  } catch (e) { /* ignore */ }
  _keepAliveEl = null;
}
